import { useCallback, useEffect } from 'react';
import { applyResetPage, collectMessageKeys } from '../../components/common/chat/timeline/timelineMerge';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useArtistEventChatMessages(context) {
  const {
    ACCESS_DENIED_STATUSES,
    ADMIN_MESSAGE_FORMATS,
    API_URL,
    EARLIEST_CREATED_AT,
    RECON_LIMIT,
    anchorHighlightTimerRef,
    chatContainerRef,
    fetchJsonSafe,
    fetchMessagesRef,
    fetchNewMessagesRef,
    focusChatAnchorMessage,
    hasNewerMessages,
    initialLoadRef,
    isFetching,
    lastDiffFetchRef,
    latestCreatedAtRef,
    limit,
    loadingNewerMessages,
    loggedInArtistId,
    mergeMessages,
    messageIdsRef,
    messageWindowOffset,
    messages,
    patchMessages,
    pickFirstString,
    pickRealtimeReadCount,
    postEventReadReceipt,
    selectedEventId,
    selectedEventIdRef,
    setActiveAnchorMessageId,
    setEvents,
    setFatalAccessError,
    setHasMore,
    setHasNewerMessages,
    setInlineCopyMessageId,
    setIsFetching,
    setIsInitialLoading,
    setLatestCreatedAt,
    setLoadedMessagesEventId,
    setLoadingNewerMessages,
    setLoadingOlderMessages,
    setMessageWindowOffset,
    messagesRef,
    setMessages,
    setMessagesError,
    setOffset,
    setSelectedEventInfo,
    setUnreadMap,
    toastError,
  } = context;
    const fetchMessages = useCallback(async (eventId, reset = false, startOffset = 0, { anchorMessageId = '' } = {}) => {
    if (!eventId || isFetching) return;
    const targetEventId = String(eventId || '').trim();
    const normalizedAnchorMessageId = reset ? String(anchorMessageId || '').trim() : '';
    // 往復中に socket で届いた分を後で拾い直すための基準。
    const keysBeforeFetch = collectMessageKeys(messagesRef?.current);
    if (reset) {
      setLoadedMessagesEventId('');
      setIsInitialLoading(true);
    }
    if (!reset) setLoadingOlderMessages(true);
    setIsFetching(true);
    const start = reset ? 0 : Math.max(0, Number.parseInt(startOffset, 10) || 0);

      try {
        setMessagesError('');
        const data = await fetchJsonSafe(
          normalizedAnchorMessageId
            ? `${API_URL}/api/event-chat/events/${eventId}/messages/around/${encodeURIComponent(
                normalizedAnchorMessageId,
              )}/25/25`
            : `${API_URL}/api/event-chat/events/${eventId}/messages/${start}/${limit}`,
          { cache: 'no-store' },
          { label: 'メッセージの取得に失敗しました。' }
        );
        const raw = Array.isArray(data?.messages) ? data.messages : [];
        if (selectedEventIdRef.current !== eventId) return;

        const mapped = raw.map(m => {
          const isAdmin = m.message_type === 'admin_message';
          const isSystem = m.message_type === 'system_message';
          const isEventCancelled =
            isSystem &&
            (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || m.message_format === 'event_cancelled');
          return {
            ...m,
            isAdminMsg: isAdmin,
            isSystemMsg: isSystem,
            isEventCancelledMsg: isEventCancelled,
            isMyArtistMsg: !isAdmin && !isSystem && m.artist_id === loggedInArtistId
          };
        });

        if (reset) {
          // mapped は wire 順(ASC、§1)。state は DESC(§3.1)なので merge で正規化する。
          // 境界カーソル(latest)は wire 順の受信ページから読む。
          setMessages((prev) => applyResetPage(prev, mapped, keysBeforeFetch));
          messageIdsRef.current = new Set(mapped.map(m => m.id));
          if (normalizedAnchorMessageId) {
            const windowOffset = Number(data?.offset || 0);
            setMessageWindowOffset(windowOffset);
            setOffset(windowOffset + mapped.length);
            setHasMore(!!data?.hasOlder);
            setHasNewerMessages(!!data?.hasNewer);
            focusChatAnchorMessage({
              messageId: normalizedAnchorMessageId,
              containerRef: chatContainerRef,
              setActiveAnchorMessageId,
              timerRef: anchorHighlightTimerRef,
              behavior: 'auto',
            });
          } else {
            setMessageWindowOffset(0);
            setOffset(mapped.length);
            setHasMore(mapped.length === limit);
            setHasNewerMessages(false);
            setActiveAnchorMessageId('');
          }
          if (mapped.length) setLatestCreatedAt(mapped[mapped.length - 1].created_at);
          lastDiffFetchRef.current = Date.now();

          // ANCHOR ジャンプ時は focusChatAnchorMessage が位置決めするため最下部固定しない(§3.4)。
          if (!normalizedAnchorMessageId && chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }

          // §3.6: ANCHOR ジャンプで開いた時は最新未到達のため既読化を保留。
          const lastId = mapped.length ? mapped[mapped.length - 1].id : null;
          if (lastId && !normalizedAnchorMessageId) {
            await postEventReadReceipt(eventId, lastId);
            setEvents(prev => prev.map(e => (e.event_uuid === eventId ? { ...e, unreadCount: 0 } : e)));
            setUnreadMap(prev => ({ ...prev, [eventId]: 0 }));
          }
        } else {
          let anchorId = null;
          let anchorTop = 0;
          const c = chatContainerRef.current;
          if (c) {
            const isReverse = (getComputedStyle(c).flexDirection || '').toLowerCase().includes('column-reverse');
            const anchorEl = isReverse ? c.lastElementChild : c.firstElementChild;
            if (anchorEl) {
              anchorId = anchorEl.getAttribute('data-mid');
              const r1 = anchorEl.getBoundingClientRect();
              const rc = c.getBoundingClientRect();
              anchorTop = r1.top - rc.top;
            }
          }

          const deduped = mapped.filter(m => !messageIdsRef.current.has(m.id));
          deduped.forEach(m => messageIdsRef.current.add(m.id));
          if (deduped.length > 0) setMessages(prev => mergeMessages(prev, deduped));

          setOffset(start + mapped.length);
          setHasMore(mapped.length === limit);

          if (c && anchorId) {
            requestAnimationFrame(() => {
              const newAnchor = c.querySelector(`[data-mid="${anchorId}"]`);
              if (!newAnchor) return;
              const r2 = newAnchor.getBoundingClientRect();
              const rc2 = c.getBoundingClientRect();
              const newTop = r2.top - rc2.top;
              const deltaPx = newTop - anchorTop;
              // §4.1.3: column-reverse の標準アンカリングで通常 deltaPx≈0。残差が 1px 超のときのみ補正。
              if (Math.abs(deltaPx) > 1) c.scrollTop += deltaPx;
              setTimeout(() => c.dispatchEvent(new Event('scroll')), 0);
            });
          }
        }
      } catch (e) {
        let resolvedError = e;
        if (normalizedAnchorMessageId && Number(e?.status) === 404) {
          try {
            const fallbackData = await fetchJsonSafe(
              `${API_URL}/api/event-chat/events/${eventId}/messages/0/${limit}`,
              { cache: 'no-store' },
              { label: 'メッセージの取得に失敗しました。' }
            );
            const fallbackRaw = Array.isArray(fallbackData?.messages) ? fallbackData.messages : [];
            if (selectedEventIdRef.current !== eventId) return;

            const fallbackMapped = fallbackRaw.map((m) => {
              const isAdmin = m.message_type === 'admin_message';
              const isSystem = m.message_type === 'system_message';
              const isEventCancelled =
                isSystem &&
                (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED ||
                  m.message_format === 'event_cancelled');
              return {
                ...m,
                isAdminMsg: isAdmin,
                isSystemMsg: isSystem,
                isEventCancelledMsg: isEventCancelled,
                isMyArtistMsg: !isAdmin && !isSystem && m.artist_id === loggedInArtistId,
              };
            });

            setMessagesError('');
            setMessages((prev) => applyResetPage(prev, fallbackMapped, keysBeforeFetch));
            messageIdsRef.current = new Set(fallbackMapped.map((m) => m.id));
            setMessageWindowOffset(0);
            setOffset(fallbackMapped.length);
            setHasMore(fallbackMapped.length === limit);
            setHasNewerMessages(false);
            setActiveAnchorMessageId('');
            if (fallbackMapped.length) {
              setLatestCreatedAt(fallbackMapped[fallbackMapped.length - 1].created_at);
            }
            lastDiffFetchRef.current = Date.now();
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }

            const lastId = fallbackMapped.length ? fallbackMapped[fallbackMapped.length - 1].id : null;
            if (lastId) {
              await postEventReadReceipt(eventId, lastId);
              setEvents((prev) =>
                prev.map((row) => (row.event_uuid === eventId ? { ...row, unreadCount: 0 } : row)),
              );
              setUnreadMap((prev) => ({ ...prev, [eventId]: 0 }));
            }

            toastError('対象メッセージが見つからないため、最新メッセージを表示しました。');
            return;
          } catch (fallbackError) {
            resolvedError = fallbackError;
          }
        }
        setMessagesError(resolvedError.message);
        if (ACCESS_DENIED_STATUSES.has(resolvedError.status)) {
          setFatalAccessError(true);
          setSelectedEventInfo(null);
          setMessages([]);
          toastError('このイベントにアクセスする権限がありません。');
        } else {
          toastError(resolvedError.message || 'メッセージの取得に失敗しました。');
        }
      } finally {
        if (!reset) setLoadingOlderMessages(false);
        if (reset && String(selectedEventIdRef.current || '').trim() === targetEventId) {
          setLoadedMessagesEventId(targetEventId);
        }
        setIsFetching(false);
        if (reset) setIsInitialLoading(false);
      }
    }, [isFetching, limit, loggedInArtistId, postEventReadReceipt, toastError]);
    fetchMessagesRef.current = fetchMessages;

    useEffect(() => {
      if (!selectedEventId) return;
      if (isFetching) return;
      if (messages.length > 0) return;
      if (initialLoadRef.current.has(selectedEventId)) return;
      initialLoadRef.current.add(selectedEventId);
      fetchMessages(selectedEventId, true);
    }, [fetchMessages, selectedEventId, isFetching, messages.length]);

    const loadNewerMessages = useCallback(async () => {
      if (
        !selectedEventId ||
        loadingNewerMessages ||
        isFetching ||
        !hasNewerMessages
      ) {
        return;
      }

      const nextOffset = Math.max(0, messageWindowOffset - limit);
      const fetchLimit = Math.max(1, messageWindowOffset - nextOffset);
      const c = chatContainerRef.current;
      let anchorId = null;
      let anchorTop = 0;
      if (c) {
        const cRect = c.getBoundingClientRect();
        const rows = Array.from(c.querySelectorAll('[data-mid]'));
        const firstVisible = rows.find((el) => el.getBoundingClientRect().top >= cRect.top - 1);
        if (firstVisible) {
          anchorId = firstVisible.getAttribute('data-mid');
          anchorTop = firstVisible.getBoundingClientRect().top;
        }
      }

      setLoadingNewerMessages(true);
      try {
        const data = await fetchJsonSafe(
          `${API_URL}/api/event-chat/events/${selectedEventId}/messages/${nextOffset}/${fetchLimit}`,
          { cache: 'no-store' },
          { label: '新しいメッセージの取得に失敗しました。' }
        );
        const raw = Array.isArray(data?.messages) ? data.messages : [];
        const mapped = raw.map((m) => {
          const isAdmin = m.message_type === 'admin_message';
          const isSystem = m.message_type === 'system_message';
          const isEventCancelled =
            isSystem &&
            (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || m.message_format === 'event_cancelled');
          return {
            ...m,
            isAdminMsg: isAdmin,
            isSystemMsg: isSystem,
            isEventCancelledMsg: isEventCancelled,
            isMyArtistMsg: !isAdmin && !isSystem && m.artist_id === loggedInArtistId
          };
        });
        const deduped = mapped.filter((message) => !messageIdsRef.current.has(message.id));
        deduped.forEach((message) => messageIdsRef.current.add(message.id));
        if (deduped.length > 0) {
          setMessages((prev) => mergeMessages(prev, deduped));
        }
        if (mapped.length > 0) {
          setLatestCreatedAt(mapped[mapped.length - 1].created_at);
        }
        setMessageWindowOffset(nextOffset);
        setHasNewerMessages(nextOffset > 0);

        requestAnimationFrame(() => {
          if (!c || !anchorId) return;
          const sameRow = c.querySelector(`[data-mid="${anchorId}"]`);
          if (!sameRow) return;
          const r2 = sameRow.getBoundingClientRect();
          const deltaPx = r2.top - anchorTop;
          // §4.1.3: column-reverse の標準アンカリングで通常 deltaPx≈0。残差が 1px 超のときのみ補正。
          if (Math.abs(deltaPx) > 1) c.scrollTop += deltaPx;
        });
      } catch (e) {
        setMessagesError(e.message);
        if (ACCESS_DENIED_STATUSES.has(e.status)) {
          setFatalAccessError(true);
        }
      } finally {
        setLoadingNewerMessages(false);
      }
    }, [
      hasNewerMessages,
      isFetching,
      limit,
      loadingNewerMessages,
      loggedInArtistId,
      messageWindowOffset,
      selectedEventId,
    ]);

    const fetchNewMessages = useCallback(async () => {
      const targetEventId = selectedEventIdRef.current;
      if (!targetEventId) return [];
      const since = latestCreatedAtRef.current || EARLIEST_CREATED_AT;

      try {
        setMessagesError('');
        const data = await fetchJsonSafe(
          `${API_URL}/api/event-chat/events/${targetEventId}/messages/new/${encodeURIComponent(since)}`,
          { cache: 'no-store' },
          { label: '新着メッセージの取得に失敗しました。' }
        );
        if (!Array.isArray(data.messages) || data.messages.length === 0) return [];

        if (selectedEventIdRef.current !== targetEventId) return [];

        const mapped = data.messages.map(m => {
          const isAdmin = m.message_type === 'admin_message';
          const isSystem = m.message_type === 'system_message';
          const isEventCancelled =
            isSystem &&
            (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || m.message_format === 'event_cancelled');
          return {
            ...m,
            isAdminMsg: isAdmin,
            isSystemMsg: isSystem,
            isEventCancelledMsg: isEventCancelled,
            isMyArtistMsg: !isAdmin && !isSystem && m.artist_id === loggedInArtistId
          };
        });

        // 既存分はフィールド上書き、新規分は追加 — まとめて merge。
        const fresh = mapped.filter(m => {
          if (messageIdsRef.current.has(m.id)) return false;
          messageIdsRef.current.add(m.id);
          return true;
        });
        setMessages(prev => mergeMessages(prev, mapped));

        const lastCreated = mapped[mapped.length - 1].created_at;
        setLatestCreatedAt(lastCreated);
        setOffset(prev => prev + fresh.length);

        const c = chatContainerRef.current;
        if (c) setTimeout(() => c.dispatchEvent(new Event('scroll')), 0);

        lastDiffFetchRef.current = Date.now();
        return mapped;
      } catch (e) {
        setMessagesError(e.message);
        if (ACCESS_DENIED_STATUSES.has(e.status)) {
          setFatalAccessError(true);
          setSelectedEventInfo(null);
          setMessages([]);
          toastError('このイベントにアクセスする権限がありません。');
        } else {
          toastError(e.message || '新着メッセージの取得に失敗しました。');
        }
        return [];
      }
    }, [loggedInArtistId, toastError]);
    useEffect(() => {
      fetchMessagesRef.current = fetchMessages;
    }, [fetchMessages]);
    useEffect(() => {
      fetchNewMessagesRef.current = fetchNewMessages;
    }, [fetchNewMessages]);

    useEffect(() => {
      setInlineCopyMessageId('');
    }, [selectedEventId]);

    const reconcileRecentMessages = useCallback(async () => {
      if (!selectedEventId) return;
      try {
        const r = await fetchJsonSafe(
          `${API_URL}/api/event-chat/events/${selectedEventId}/messages/0/${Math.max(limit, RECON_LIMIT)}`,
          {},
          { label: 'メッセージの更新に失敗しました。' }
        );
        const latest = r?.messages || [];
        const mapped = latest.map(m => {
          const isAdmin = m.message_type === 'admin_message';
          const isSystem = m.message_type === 'system_message';
          const isEventCancelled =
            isSystem &&
            (m.message_format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || m.message_format === 'event_cancelled');
          return {
            ...m,
            isAdminMsg: isAdmin,
            isSystemMsg: isSystem,
            isEventCancelledMsg: isEventCancelled,
            isMyArtistMsg: !isAdmin && !isSystem && m.artist_id === loggedInArtistId
          };
        });
        const map = new Map(mapped.map(m => [m.id, m]));
        // read receipt 等の後勝ちフィールドを既存メッセージへ patch のみ(新規追加はしない)。
        setMessages(prev => patchMessages(prev, [...map.keys()], (m) => map.get(m.id) || {}));
        if (mapped.length) {
          setLatestCreatedAt(mapped[mapped.length - 1].created_at);
        }
      } catch { /* no-op */ }
    }, [selectedEventId, loggedInArtistId]);

    const buildRealtimeEventTextMessage = useCallback((payload = {}, fallbackEventId = '') => {
      if (payload?.question) return null;
      const format = String(payload?.messageFormat || payload?.message_format || 'text').trim().toLowerCase();
      const messageId = String(payload?.messageId || payload?.message_id || '').trim();
      const content = pickFirstString(
        payload?.content,
        payload?.text,
        payload?.preview,
        payload?.messagePreview,
        payload?.message_preview,
      );
      if (format !== 'text' || !messageId || !content) return null;
      const senderType = String(payload?.senderType || payload?.sender_type || '').trim().toLowerCase();
      const messageType =
        String(payload?.messageType || payload?.message_type || '').trim().toLowerCase() ||
        (senderType === 'admin' ? 'admin_message' : 'artist_message');
      const senderId = payload?.senderId || payload?.sender_id || null;
      const adminUserId =
        messageType === 'admin_message'
          ? payload?.senderAdminId ||
            payload?.sender_admin_id ||
            payload?.adminUserId ||
            payload?.admin_user_id ||
            senderId ||
            null
          : null;
      const artistId =
        messageType === 'artist_message'
          ? payload?.senderArtistId ||
            payload?.sender_artist_id ||
            payload?.artistId ||
            payload?.artist_id ||
            senderId ||
            null
          : null;
      const isAdmin = messageType === 'admin_message';
      return {
        id: messageId,
        event_id: payload?.eventId || payload?.event_id || fallbackEventId || null,
        event_uuid: payload?.eventId || payload?.event_id || fallbackEventId || null,
        source_event_id: payload?.eventId || payload?.event_id || fallbackEventId || null,
        thread_id: payload?.threadId || payload?.thread_id || null,
        message_type: messageType,
        message_format: 'text',
        content,
        admin_user_id: adminUserId,
        artist_id: artistId,
        created_at: new Date().toISOString(),
        is_read: false,
        read_count: pickRealtimeReadCount(payload, 1),
        reactions: [],
        mentions: Array.isArray(payload?.mentions) ? payload.mentions : [],
        isAdminMsg: isAdmin,
        isSystemMsg: false,
        isEventCancelledMsg: false,
        isMyArtistMsg: !isAdmin && String(artistId || '') === String(loggedInArtistId || ''),
      };
    }, [loggedInArtistId]);

    // NOTE(§4.1.2): 旧 scheduleRealtimeEventReconcile(700ms 後の最新ページ再取得 merge)は廃止。
    // socket 受信は enriched=直接 merge / skeleton=messages/new 差分 fetch の 2 経路に集約。
    // read receipt の reconcileRecentMessages(reconcileTimerRef 経由)は別物で維持。
  return {
    fetchMessages,
    loadNewerMessages,
    fetchNewMessages,
    reconcileRecentMessages,
    buildRealtimeEventTextMessage,
  };
}
